import { faChevronLeft, faChevronRight } from '@fortawesome/pro-regular-svg-icons';
import clsx from 'clsx';
import { EmblaCarouselType } from 'embla-carousel';
import React, { ComponentPropsWithRef, FC, useCallback, useEffect, useState } from 'react';
import { Icon, IconButton } from '@/components/ui';

type UsePrevNextButtonsType = {
  prevBtnDisabled: boolean;
  nextBtnDisabled: boolean;
  onPrevButtonClick: () => void;
  onNextButtonClick: () => void;
};

export const usePrevNextButtons = (
  emblaApi: EmblaCarouselType | undefined,
): UsePrevNextButtonsType => {
  const [prevBtnDisabled, setPrevBtnDisabled] = useState(true);
  const [nextBtnDisabled, setNextBtnDisabled] = useState(true);

  const onPrevButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollPrev();
  }, [emblaApi]);

  const onNextButtonClick = useCallback(() => {
    if (!emblaApi) return;
    emblaApi.scrollNext();
  }, [emblaApi]);

  const onSelect = useCallback((emblaApi: EmblaCarouselType) => {
    setPrevBtnDisabled(!emblaApi.canScrollPrev());
    setNextBtnDisabled(!emblaApi.canScrollNext());
  }, []);

  useEffect(() => {
    if (!emblaApi) return;

    onSelect(emblaApi);
    emblaApi.on('reInit', onSelect).on('select', onSelect);
  }, [emblaApi, onSelect]);

  return {
    prevBtnDisabled,
    nextBtnDisabled,
    onPrevButtonClick,
    onNextButtonClick,
  };
};

type Props = ComponentPropsWithRef<'button'>;

const BUTTON_STYLE =
  'rounded-full border-black border-1 disabled:cursor-not-allowed disabled:text-gray-400 disabled:border-gray-400';

export const PrevButton: FC<Props> = ({ onClick, disabled, style, ...props }) => {
  return (
    <IconButton
      aria-label="Previous"
      onClick={e => {
        onClick?.(e as React.MouseEvent<HTMLButtonElement, MouseEvent>);
      }}
      variant="bordered"
      isDisabled={disabled}
      className={clsx(BUTTON_STYLE, props.className)}
      style={style}
    >
      <Icon icon={faChevronLeft} />
    </IconButton>
  );
};

export const NextButton: FC<Props> = ({ onClick, disabled, style, ...props }) => {
  return (
    <IconButton
      aria-label="Next"
      isDisabled={disabled}
      isIconOnly
      onClick={e => {
        onClick?.(e as React.MouseEvent<HTMLButtonElement, MouseEvent>);
      }}
      variant="bordered"
      className={clsx(BUTTON_STYLE, props.className)}
      style={style}
    >
      <Icon icon={faChevronRight} />
    </IconButton>
  );
};
